/**
 * FeatureLockedPage - Shown when user doesn't have access to a feature
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lock,
  ArrowLeft,
  Brain,
  Wallet,
  Heart,
  Dumbbell,
  Home,
} from 'lucide-react';
import { FEATURE_INFO } from '../stores/featureAccessStore';

// Feature icon mapping
const FEATURE_ICONS = {
  mindmap: Brain,
  cashflow: Wallet,
  moodmap: Heart,
  fittrack: Dumbbell,
};

// Feature color mapping
const FEATURE_COLORS = {
  mindmap: 'indigo',
  cashflow: 'emerald',
  moodmap: 'purple',
  fittrack: 'orange',
};

const FeatureLockedPage = ({ featureId }) => {
  const navigate = useNavigate();

  const feature = FEATURE_INFO[featureId];
  const Icon = FEATURE_ICONS[featureId] || Lock;
  const color = FEATURE_COLORS[featureId] || 'gray';

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        {/* Icon */}
        <div className={`w-20 h-20 mx-auto mb-6 bg-${color}-500/20 rounded-2xl flex items-center justify-center relative`}>
          <Icon size={40} className={`text-${color}-400`} />
          <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center border-2 border-gray-800">
            <Lock size={14} className="text-gray-500" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2">
          {feature?.name || 'Feature'} is Locked
        </h1>

        {/* Description */}
        <p className="text-gray-400 mb-6">
          This feature is not enabled for your account. Contact an administrator to request access.
        </p>

        {/* Feature info */}
        {feature && (
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              About this feature
            </h3>
            <p className="text-gray-300">
              {feature.description}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl transition-colors"
          >
            <ArrowLeft size={18} />
            Go Back
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors"
          >
            <Home size={18} />
            Dashboard
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default FeatureLockedPage;
